import { Box, Button, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow, Typography, IconButton, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { getUrl } from "src/Utils/Common";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import ColoredBox from "src/Components/Boxes/TopProductBox/ColoredBox";
import { ExpandMore, ExpandLess } from "@mui/icons-material";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      padding: "0.5px",
      color: theme.palette.primary.dark,
      fontSize: theme.typography.fontSize.xxsmall,
      textAlign: "center",
      justifyContent: "center",
      width: "auto",
      minWidth: "60px",
      maxWidth: "120px",
    },
    [`&.${tableCellClasses.body}`]: {
      textTransform: "capitalize",
      color: theme.palette.primary.dark,
      fontSize: 10,
      textAlign: "center",
      padding: "0.5px",
      width: "auto",
      minWidth: "60px",
      maxWidth: "120px",
    },
  }));

  const StyledTableRow = styled(TableRow)(() => ({
    height: 15,
  }));

function PromotionTable({
  hierarchy,
  manufacturer,
  promoType,
  setSelectedHier
}) {

  const projectId = useSelector((state) => state.userProject.projectId);
  const[data, setData] = useState([]);
  const[loading, setLoading] = useState(false);
  const[expandedRows, setExpandedRows] = useState(new Set());
  const[nestedData, setNestedData] = useState({});
  const[loadingNested, setLoadingNested] = useState(false);

  const formatNumber = (num) => {
    if (typeof num === 'number' && !isNaN(num)) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    }
    return num;
  };

  const formatNumberNoDecimals = (num) => {
    if (typeof num === 'number' && !isNaN(num)) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
    return num;
  };
    
  useEffect(()=>{
    if (!manufacturer || !hierarchy || !promoType) {
      console.warn("[PromotionTable] Missing required props", { manufacturer, hierarchy, promoType });
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const normalizedHierarchy = (hierarchy || '').toLowerCase();
    const normalizedManufacturer = manufacturer === "SAVOLA" ? "SAVOLA GROUP" : 
                                   manufacturer === "IFCO" ? "IFFCO GROUP" : manufacturer;
                                   console.log(normalizedManufacturer);
    const filtersJSON = encodeURIComponent(JSON.stringify({ manufacturer: normalizedManufacturer }));
    console.log("filtersJSON", filtersJSON);
    const url = `${getUrl()}/strategy_creation/promo_evolution?project_id=${projectId}&hierarchy=${normalizedHierarchy}&time_period=P12M&filters=${filtersJSON}&target=12&promo_type=${promoType}`;
    console.log("[PromotionTable] Fetch URL:", url);
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        "x-access-token": localStorage.getItem("userId") || "",
      },
    })
      .then((res) => res.text())
      .then((text) => {
        try {
          const cleaned = text.replace(/\bNaN\b/g, "null");
          if (!cleaned.trim().startsWith("{") && !cleaned.trim().startsWith("[")) {
            console.error("Promo API did not return JSON:", cleaned);
            setData([]);
            return;
          }
          const json = JSON.parse(cleaned);
          console.log("[PromotionTable] API Response:", JSON.stringify(json, null, 2));
          console.log("[PromotionTable] API keys:", Object.keys(json || {}));
          const labelArray = (json[normalizedHierarchy] || json.brand || []);
          const length = labelArray.length;
          const rows = [];
          const to2 = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
          };
          for (let i = 0; i < length; i++) {
            const item = {
              manufacturer: manufacturer,
              [hierarchy]: labelArray[i],
              brand: (json.brand || [])[i],
              year: (json.year || [])[i],
              value_sales: to2((json.value_sales || [])[i]),
              volume_sales: to2((json.volume_sales || [])[i]),
              volume_sales_non_promo: to2((json.volume_sales_non_promo || [])[i]),
              VSOD: to2((json.VSOD || [])[i]),
              count_PX: to2((json[`count_${promoType}`] || [])[i]),
              value_sales_PX: to2((json[`value_sales_${promoType}`] || [])[i]),
              volume_sales_PX: to2((json[`volume_sales_${promoType}`] || [])[i]),
              uplift_volume_PX: to2((json[`uplift_volume_${promoType}`] || [])[i]),
              discount_PX: to2((json.discount_pct || [])[i]),
              productivity_PX: to2((json[`productivity_${promoType}`] || [])[i]),
              baseline_volume: to2((json.baseline_volume || [])[i]),
              uplift_volume: to2((json.uplift_volume || [])[i]),
              uplift_pct: to2((json.uplift_pct || [])[i]),
              avg_productivity: to2((json[`average_productivity_${promoType}`] || [])[i])
            };
            rows.push(item);
          }
          console.log("[PromotionTable] Rows built:", rows);
    setData(rows);
        } catch (e) {
          console.error("Promo API parse error:", e);
          setData([]);
        }
      })
      .catch((err) => {
        console.error("PromotionTable fetch error:", err);
        setData([]);
      })
      .finally(() => setLoading(false));
  },
[manufacturer, hierarchy, promoType, projectId]
)

  useEffect(() => {
    setExpandedRows(new Set());
    setNestedData({});
  }, [hierarchy]);

  const fetchNestedTableData = (targetHierarchy, categoryFilter) => {
    setLoadingNested(true);
    const normalizedManufacturer = manufacturer === "SAVOLA" ? "SAVOLA GROUP" : 
                                   manufacturer === "IFCO" ? "IFFCO GROUP" : manufacturer;
    const filtersObj = { manufacturer: normalizedManufacturer, [hierarchy]: categoryFilter };
    const filtersJSON = encodeURIComponent(JSON.stringify(filtersObj));
    const url = `${getUrl()}/strategy_creation/promo_evolution?project_id=${projectId}&hierarchy=${targetHierarchy}&time_period=P12M&filters=${filtersJSON}&target=12&promo_type=${promoType}`;
    
    console.log("[PromotionTable] Fetching nested data:", url);
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        "x-access-token": localStorage.getItem("userId") || "",
      },
    })
      .then((res) => res.text())
      .then((text) => {
        try {
          const cleaned = text.replace(/\bNaN\b/g, "null");
          if (!cleaned.trim().startsWith("{") && !cleaned.trim().startsWith("[")) {
            console.error("Nested API did not return JSON:", cleaned);
            return;
          }
          const json = JSON.parse(cleaned);
          console.log("[PromotionTable] Nested API Response:", JSON.stringify(json, null, 2));
          
          const labelArray = (json[targetHierarchy] || []);
          const length = labelArray.length;
          const rows = [];
          const to2 = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
          };
          
          for (let i = 0; i < length; i++) {
            const item = {
              manufacturer: manufacturer,
              [targetHierarchy]: labelArray[i],
              brand: (json.brand || [])[i],
              year: (json.year || [])[i],
              value_sales: to2((json.value_sales || [])[i]),
              volume_sales: to2((json.volume_sales || [])[i]),
              volume_sales_non_promo: to2((json.volume_sales_non_promo || [])[i]),
              VSOD: to2((json.VSOD || [])[i]),
              count_PX: to2((json[`count_${promoType}`] || [])[i]),
              value_sales_PX: to2((json[`value_sales_${promoType}`] || [])[i]),
              volume_sales_PX: to2((json[`volume_sales_${promoType}`] || [])[i]),
              uplift_volume_PX: to2((json[`uplift_volume_${promoType}`] || [])[i]),
              discount_PX: to2((json.discount_pct || [])[i]),
              productivity_PX: to2((json[`productivity_${promoType}`] || [])[i]),
              baseline_volume: to2((json.baseline_volume || [])[i]),
              uplift_volume: to2((json.uplift_volume || [])[i]),
              uplift_pct: to2((json.uplift_pct || [])[i]),
              avg_productivity: to2((json[`average_productivity_${promoType}`] || [])[i])
            };
            rows.push(item);
          }
          
          setNestedData(prev => ({
            ...prev,
            [categoryFilter]: rows
          }));
        } catch (e) {
          console.error("Nested API parse error:", e);
        }
      })
      .catch((err) => {
        console.error("Nested fetch error:", err);
      })
      .finally(() => setLoadingNested(false));
  };

  const handleRowToggle = (categoryName, rowIndex) => {
    const newExpandedRows = new Set(expandedRows);
    
    if (expandedRows.has(rowIndex)) {
      newExpandedRows.delete(rowIndex);
    } else {
      newExpandedRows.add(rowIndex);
      if (!nestedData[categoryName]) {
        const targetHierarchy = hierarchy === "brand" ? "class" : "brand";
        fetchNestedTableData(targetHierarchy, categoryName);
      }
    }
    setExpandedRows(newExpandedRows);
  };


    return(
        <>
            <Typography textAlign="center">
                  Promotion Evaluation
            </Typography>
            {loading ? (
              <Box display="flex" alignItems="center" justifyContent="center" sx={{ py: 2 }}>
                <FacebookCircularProgress />
              </Box>
            ) : data.length === 0 ? (
              <Box display="flex" alignItems="center" justifyContent="center" sx={{ py: 2 }}>
                <FacebookCircularProgress loading={false} hasData={false} message="No data for selected filters" />
              </Box>
            ) : (
            <TableContainer
                    sx={{
                      maxHeight: 600,
                      overflowY: "auto",
                      overflowX: "auto",
                      WebkitOverflowScrolling: "touch",
                      touchAction: "manipulation",
                      backgroundColor: "#fff",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                    }}
                  >
                    <Table stickyHeader aria-label="customized table">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f1f1f1" }}>
                          <StyledTableCell>#</StyledTableCell>
                          <StyledTableCell>{hierarchy}</StyledTableCell>
                          <StyledTableCell>Value Sales</StyledTableCell>
                          <StyledTableCell>Volume Sales</StyledTableCell>
                          <StyledTableCell>Volume Sales Non Promo</StyledTableCell>
                          <StyledTableCell>VSOD</StyledTableCell>
                          <StyledTableCell>Count {promoType}</StyledTableCell>
                          <StyledTableCell>Value Sales {promoType}</StyledTableCell>
                          <StyledTableCell>Volume Sales {promoType}</StyledTableCell>
                          <StyledTableCell>Discount {promoType}</StyledTableCell>
                          <StyledTableCell>
                            <Tooltip title="Productivity is calculated based on the avg promo volume sold" arrow>
                              Productivity {promoType}
                            </Tooltip>
                          </StyledTableCell>
                          <StyledTableCell>Actions</StyledTableCell>
                        </TableRow>
                      </TableHead>
        
                      <TableBody>
                        {data.map((item, idx) => {
                          const cellSx = { fontWeight: "bold" };
                          const isExpanded = expandedRows.has(idx);
                          const targetHierarchy = hierarchy === "brand" ? "class" : "brand";
        
                          return (
                            <React.Fragment key={idx}>
                              <StyledTableRow>
                                <StyledTableCell sx={cellSx}>{idx + 1}</StyledTableCell>
                                <StyledTableCell sx={{...cellSx, textAlign: "left"}}>
                                  <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRowToggle(item[hierarchy], idx)}
                                      sx={{ 
                                        padding: 0.5,
                                        color: "#1976d2",
                                        "&:hover": {
                                          backgroundColor: "#e3f2fd",
                                          color: "#1565c0",
                                        },
                                      }}
                                    >
                                      {isExpanded ? (
                                        <ExpandLess sx={{ fontSize: "1rem" }} />
                                      ) : (
                                        <ExpandMore sx={{ fontSize: "1rem" }} />
                                      )}
                                    </IconButton>
                                    <Box sx={{ textAlign: "left", width: "100%" }}>
                                      {item[hierarchy]}
                                    </Box>
                                  </Box>
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                  {formatNumberNoDecimals(item.value_sales)}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                  {formatNumberNoDecimals(item.volume_sales)}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                  {formatNumberNoDecimals(item.volume_sales_non_promo)}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                  {formatNumberNoDecimals(item.VSOD)}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                  {formatNumberNoDecimals(item.count_PX)}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                  {formatNumberNoDecimals(item.value_sales_PX)}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                  {formatNumberNoDecimals(item.volume_sales_PX)}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                  {formatNumberNoDecimals(item.discount_PX)}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                  <ColoredBox
                                    number={formatNumber(item.productivity_PX)}
                                    Color={
                                      item.productivity_PX > item.avg_productivity
                                        ? "#0BB783"
                                        : item.productivity_PX === item.avg_productivity
                                        ? "#0095FF"
                                        : "#B22234"
                                    }
                                  />
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                    <Box display="flex" gap={1} sx={{ padding: "8px" }}>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        sx={{
                                          fontSize: "0.7rem",
                                          padding: "2px 8px",
                                          minWidth: "auto",
                                        }}
                                        onClick={() => {
                                          console.log(hierarchy)
                                          console.log(item)
                                          if (hierarchy === "brand") {
                                            setSelectedHier(item[hierarchy], null);
                                          } else {
                                            setSelectedHier(null, item[hierarchy]);
                                          }
                                          window.scrollTo({
                                            top: document.body.scrollHeight, // scroll to bottom
                                            behavior: "smooth", // smooth scroll
                                          });
                                        }}
                                      >
                                          View SKUs
                                      </Button>
                                    </Box>
                                  
                                </StyledTableCell>
                              </StyledTableRow>
                              
                              {/* Render nested rows */}
                              {isExpanded && (
                                loadingNested ? (
                                  <StyledTableRow>
                                    <StyledTableCell colSpan={14} align="center" sx={{ py: 2 }}>
                                      <FacebookCircularProgress />
                                    </StyledTableCell>
                                  </StyledTableRow>
                                ) : (
                                  nestedData[item[hierarchy]] &&
                                  nestedData[item[hierarchy]].map((nestedItem, nestedIdx) => (
                                    <StyledTableRow 
                                      key={`nested-${idx}-${nestedIdx}`}
                                      sx={{ backgroundColor: "#f0f0f0" }}
                                    >
                                      <StyledTableCell sx={{ paddingLeft: 4 }}>{nestedIdx + 1}</StyledTableCell>
                                      <StyledTableCell sx={{ paddingLeft: 4, textAlign: "left" }}>
                                        <Box sx={{ textAlign: "left", width: "100%" }}>
                                          {nestedItem[targetHierarchy]}
                                        </Box>
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {formatNumberNoDecimals(nestedItem.value_sales)}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {formatNumberNoDecimals(nestedItem.volume_sales)}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {formatNumberNoDecimals(nestedItem.volume_sales_non_promo)}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {formatNumberNoDecimals(nestedItem.VSOD)}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {formatNumberNoDecimals(nestedItem.count_PX)}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {formatNumberNoDecimals(nestedItem.value_sales_PX)}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {formatNumberNoDecimals(nestedItem.volume_sales_PX)}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {formatNumberNoDecimals(nestedItem.discount_PX)}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        <ColoredBox
                                          number={formatNumber(nestedItem.productivity_PX)}
                                          Color={
                                            nestedItem.productivity_PX > nestedItem.avg_productivity
                                              ? "#0BB783"
                                              : nestedItem.productivity_PX === nestedItem.avg_productivity
                                              ? "#0095FF"
                                              : "#B22234"
                                          }
                                        />
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        <Box display="flex" gap={1} sx={{ padding: "8px" }}>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            sx={{
                                              fontSize: "0.7rem",
                                              padding: "2px 8px",
                                              minWidth: "auto",
                                            }}
                                            onClick={() => {
                                              console.log("Nested item clicked:", nestedItem);
                                              if (hierarchy === "brand") {
                                                setSelectedHier(item[hierarchy], nestedItem[targetHierarchy]);
                                              } else {
                                                setSelectedHier(nestedItem[targetHierarchy], item[hierarchy]);
                                              }
                                            }}
                                          >
                                            View SKUs
                                          </Button>
                                        </Box>
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  ))
                                )
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
            )}
        
                </>
    )
}

export default PromotionTable

PromotionTable.propTypes = {
  hierarchy: PropTypes.string.isRequired,
  manufacturer: PropTypes.string.isRequired,
  promoType: PropTypes.string.isRequired,
  setSelectedHier: PropTypes.func.isRequired,
};